import type { PhaseProject, PhaseProjectInput } from "@/lib/types";
import { z } from "zod";
import type { ProjectPhaseDraft } from "./PhaseProjectBuilder";

export interface ProjectDraft extends PhaseProjectInput {
  phases: ProjectPhaseDraft[];
}

export const formSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, { message: "Project title is required" })
    .max(200, { message: "Project title must be 200 characters or less" }),
});

export type FormValues = z.infer<typeof formSchema>;

export const projectToDraft = (project?: PhaseProject): ProjectDraft => {
  if (!project) {
    return {
      title: "",
      description: null,
      incidentTypeIds: [],
      phases: [],
      actionTaskTemplates: {},
    };
  }
  return {
    actionTaskTemplates: Object.fromEntries(
      project.currentRevision.phases.flatMap((phase) =>
        phase.actions.map((action) => [
          action.actionDefinitionId,
          action.taskTemplates ?? [],
        ]),
      ),
    ),
    title: project.title,
    description: project.description,
    incidentTypeIds: project.currentRevision.incidentTypeIds,
    phases: [...project.currentRevision.phases]
      .sort((a, b) => a.displayPosition - b.displayPosition)
      .map((phase) => ({
        localId: phase.id,
        reusablePhaseId: phase.reusablePhaseId,
        teamId: phase.teamId ?? null,
        teamLabel: phase.teamLabel ?? null,
        userId: phase.userId ?? null,
        userLabel: phase.userLabel ?? null,
        roleId: phase.roleId ?? null,
        roleLabel: phase.roleLabel ?? null,
        userAvailable: phase.userAvailable ?? true,
        claimTypeIds: [...phase.claimTypeIds],
        widgets: (phase.widgets ?? []).map((widget) => ({
          ...widget,
          actionDefinitionIds: [...widget.actionDefinitionIds],
        })),
        actionDefinitionIds: [...phase.actions]
          .sort((a, b) => a.displayPosition - b.displayPosition)
          .map((action) => action.actionDefinitionId),
        actionClaimTypeIds: Object.fromEntries(
          phase.actions.map((action) => [
            action.actionDefinitionId,
            [...action.claimTypeIds],
          ]),
        ),
      })),
  };
};
